import React, { useState } from 'react';
import { useDataProvider, useGetIdentity, useRefresh } from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  TextField,
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { DEFAULTS } from '../../constants';

interface DivisionRow {
  id: number;
  division_number: string;
  division_name: string;
  division_display_number: string;
}

interface BulkAddDivisionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedClient: any;
}

const BulkAddDivisionsDialog: React.FC<BulkAddDivisionsDialogProps> = ({ isOpen, onClose, selectedClient }) => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();

  const [divisionRows, setDivisionRows] = useState<DivisionRow[]>([]);
  const [duplicateRows, setDuplicateRows] = useState<string[]>([]);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [importMode, setImportMode] = useState<'append' | 'replace'>('append');

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  const isDataInvalid = divisionRows.some(
    (row) => !row.division_number.trim() || !row.division_name.trim() || !row.division_display_number.trim()
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);
    setWarningMessage(null);
    setDivisionRows([]);
    setDuplicateRows([]);

    if (file && selectedClient) {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          const headers = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] as string[];

          if (!headers.includes('division_number') || !headers.includes('division_name')) {
            setErrorMessage('Missing required columns: division_number or division_name');
            return;
          }

          const json = XLSX.utils.sheet_to_json(worksheet);

          const cleanedRows = json
            .filter((row: any) => row.division_number && String(row.division_number).trim() !== '')
            .map((row: any, index: number) => {
              const divNum = String(row.division_number || '').trim();
              return {
                id: index,
                division_number: divNum,
                division_name: String(row.division_name || '').trim(),
                division_display_number: divNum,
              };
            });

          if (cleanedRows.length === 0) {
            setErrorMessage('The uploaded file appears to be empty.');
            return;
          }

          const seenNumbers = new Set();
          const internalDuplicates = new Set<string>();

          cleanedRows.forEach((row) => {
            if (seenNumbers.has(row.division_number)) {
              internalDuplicates.add(row.division_number);
            }
            seenNumbers.add(row.division_number);
          });

          if (internalDuplicates.size > 0) {
            setErrorMessage(
              `Duplicate division numbers found in file: ${Array.from(internalDuplicates).join(
                ', '
              )}. Please fix the Excel and re-upload.`
            );
            return;
          }

          setDivisionRows(cleanedRows);
          await revalidateWithBackend(cleanedRows);
        } catch (err) {
          setErrorMessage('Validation failed. Please check the file format or try again.');
        } finally {
          event.target.value = '';
        }
      };

      reader.readAsBinaryString(file);
    }
  };

  const handleRowChange = (id: number, field: keyof DivisionRow, value: string) => {
    setDivisionRows((prev) => {
      const newRows = prev.map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };
          if (field === 'division_number' && row.division_display_number === row.division_number) {
            updatedRow.division_display_number = value;
          }
          return updatedRow;
        }
        return row;
      });

      const hasEmptyRequired = newRows.some(
        (r) => !r.division_number.trim() || !r.division_name.trim() || !r.division_display_number.trim()
      );

      const numbers = newRows.map((r) => r.division_number.trim()).filter(Boolean);
      const hasDuplicates = new Set(numbers).size !== numbers.length;

      if (hasEmptyRequired) {
        setErrorMessage('One or more required fields are empty. Please fill them in to save.');
      } else if (hasDuplicates) {
        setErrorMessage('Duplicate division numbers found in the grid. Each must be unique.');
      } else {
        setErrorMessage(null);
      }

      return newRows;
    });
  };

  const revalidateWithBackend = async (rows: DivisionRow[]) => {
    const numbersToCheck = rows.map((row) => row.division_number.trim()).filter(Boolean);
    if (numbersToCheck.length === 0) return;

    try {
      const { data: duplicates } = await dataProvider.getList('ValidateDivisions', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          billing_client_number: selectedClient.billing_client_number,
          division_numbers: numbersToCheck,
        },
      });

      const duplicateNumbers = duplicates.map((item: any) => item.id);
      setDuplicateRows(duplicateNumbers);

      if (duplicateNumbers.length > 0) {
        setWarningMessage(`${duplicateNumbers.length} existing division(s) found. They will be updated.`);
      } else {
        setWarningMessage(null);
      }
    } catch (err) {}
  };

  const executeSave = async () => {
    setConfirmOpen(false);

    if (!selectedClient || divisionRows.length === 0) {
      toast.info('No divisions to save.');
      return;
    }

    const payload = {
      import_mode: importMode,
      billing_client_number: selectedClient.billing_client_number,
      billing_client_name: selectedClient.billing_client_name,
      divisions: divisionRows.map((row) => ({
        division_number: row.division_number.trim(),
        division_name: row.division_name.trim(),
        division_display_number: row.division_display_number.trim(),
        created_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
        last_modified_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
      })),
    };

    try {
      const { data: result } = await dataProvider.create('BulkAddDivisions', { data: payload });
      toast.success(result.message || 'Divisions saved successfully!');
      refresh();
      onClose();
    } catch (error: any) {
      toast.error(`Save failed: ${error.message || 'Unknown error'}`);
    }
  };

  const handleSaveClick = () => {
    if (isDataInvalid || errorMessage) return;
    if (duplicateRows.length > 0 || importMode === 'replace') {
      setConfirmOpen(true);
    } else {
      executeSave();
    }
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Bulk Add Divisions</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} alignItems="center" mb={3}>
            <TextField label="Client Name" value={selectedClient?.billing_client_name || ''} fullWidth disabled />
            <TextField
              label="Client Number"
              value={selectedClient?.billing_client_number || ''}
              disabled
              sx={{ width: '200px' }}
            />
          </Box>

          <Typography variant="h6" gutterBottom>
            Upload Excel
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Expected columns: `division_number` and `division_name`
          </Typography>
          <Button variant="outlined" startIcon={<UploadFileIcon />} component="label" sx={{ mt: 1 }}>
            Upload File
            <input type="file" accept=".xlsx" hidden onChange={handleFileChange} />
          </Button>

          {divisionRows.length > 0 && (
            <Box
              mt={3}
              mb={2}
              p={2.5}
              sx={{ backgroundColor: 'transparent', borderRadius: 2, border: '1px solid #e0e0e0' }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Upload Preference
              </Typography>
              <Box display="flex" gap={6}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={importMode === 'append'}
                    onChange={() => setImportMode('append')}
                    style={{ marginRight: '10px', accentColor: '#00838f' }}
                  />
                  <Typography variant="body2">
                    <strong>Append / Update:</strong> Adds new records and updates existing ones highlighted in red.
                  </Typography>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                    style={{ marginRight: '10px', accentColor: '#00838f' }}
                  />
                  <Typography variant="body2">
                    <strong>Replace All:</strong> Removes all existing records for this client and replaces them with
                    the data below.
                  </Typography>
                </label>
              </Box>
            </Box>
          )}

          {divisionRows.length > 0 && (
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Review and edit Divisions
              </Typography>
              <Paper>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Division Number</TableCell>
                      <TableCell>Display Number</TableCell>
                      <TableCell>Division Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {divisionRows.map((row) => {
                      const isDuplicate = duplicateRows.includes(row.division_number);
                      const shouldHighlight = importMode === 'append' && isDuplicate;
                      return (
                        <TableRow
                          key={row.id}
                          sx={{ backgroundColor: shouldHighlight ? 'rgba(255, 204, 204, 0.5)' : 'transparent' }}
                        >
                          <TableCell>
                            <TextField
                              variant="standard"
                              fullWidth
                              value={row.division_number}
                              onChange={(e) => handleRowChange(row.id, 'division_number', e.target.value)}
                              onBlur={() => revalidateWithBackend(divisionRows)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              fullWidth
                              value={row.division_display_number}
                              onChange={(e) => handleRowChange(row.id, 'division_display_number', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              fullWidth
                              value={row.division_name}
                              onChange={(e) => handleRowChange(row.id, 'division_name', e.target.value)}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}

          {errorMessage && (
            <Box mt={2} p={1.5} sx={{ backgroundColor: '#fff5f5', border: '1px solid #ffcccc', borderRadius: 1 }}>
              <Typography color="error" variant="body2" sx={{ fontWeight: 500 }}>
                {errorMessage}
              </Typography>
            </Box>
          )}
          {warningMessage && !errorMessage && importMode === 'append' && (
            <Box mt={2} p={1.5} sx={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 1 }}>
              <Typography sx={{ color: '#92400e', fontSize: '0.875rem', fontWeight: 500 }}>
                ⚠️ {warningMessage}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSaveClick}
            variant="contained"
            disabled={divisionRows.length === 0 || isDataInvalid || !!errorMessage}
          >
            Save Divisions
          </Button>
        </DialogActions>
      </Dialog>

      {isConfirmOpen && (
        <Dialog open={isConfirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle sx={{ color: importMode === 'replace' ? 'error.main' : 'inherit' }}>
            {importMode === 'replace' ? '⚠️ Confirm Replace All' : 'Confirm Save'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {importMode === 'replace'
                ? `Warning: This will DELETE ALL existing divisions for ${selectedClient?.billing_client_name} and replace them with these ${divisionRows.length} records. This action cannot be undone.`
                : `This will add ${divisionRows.length} records. ${duplicateRows.length} existing records (highlighted in red) will be updated with the new data. Proceed?`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={executeSave}
              variant="contained"
              color={importMode === 'replace' ? 'error' : 'primary'}
              autoFocus
            >
              Confirm & Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default BulkAddDivisionsDialog;
